import React, { Component } from 'react';
import PropTypes from 'prop-types';
import AsyncSelect from 'react-select/async';
import ModalTrigger from '../../common/ModalTrigger';
import { putRequest, getRequest } from '../../../requestUtils';
import DropdownIndicator from '../../common/DropdownIndicator';
import customStyles from '../../../dropdownStyleUtils';
import TextAreaInput from '../../common/TextAreaInput';
import './ResumeActions.scss';

const submitDisabled = (action, internalFeedbacks, feedbackText, message) => (
  feedbackText.length === 0
  || (action === 'Decline' && (
    message.length === 0
    || internalFeedbacks.length === 0
    || (internalFeedbacks
    && internalFeedbacks.map((r) => r.label).includes('Other')
    && feedbackText === '')
  ))
);

const internalFeedbackOptions = () => (
  getRequest(
    '/internal_feedbacks/',
  ).then((response) => (
    response.data.map((i) => ({
      label: i.name,
      value: i.id,
    }))
  ))
);

class ResumeActions extends Component {
  constructor(props) {
    super(props);
    this.state = {
      message: this.templateMessage(),
      internalFeedbacks: props.internalFeedbacks,
      feedbackText: props.feedbackText,
      sendEmail: true,
    };

    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  handleInputChange(name, value) {
    this.setState({
      [name]: value,
    });
  }

  handleSubmit() {
    const {
      action, formUrl, csrfToken, afterSubmit, interestId,
    } = this.props;
    const {
      message, internalFeedbacks, feedbackText, sendEmail,
    } = this.state;
    const params = {
      rep_action: action.toLowerCase(),
      id: interestId,
      send_email: sendEmail,
      message,
      internal_feedback_ids: internalFeedbacks.map((r) => r.value),
      internal_feedback_text: feedbackText,
    };

    putRequest(
      formUrl,
      params,
      csrfToken,
    ).then((response) => {
      afterSubmit(response.data);
    });
  }

  actionVerb() {
    const { action } = this.props;
    switch (action) {
      case 'Decline':
        return 'declining';
      case 'Waitlist':
        return 'waitlisting';
      case 'Accept':
        return 'accepting';
      default:
        return null;
    }
  }

  templateMessage() {
    const {
      consultant, project, action, opportunitiesUrl, projectUrl, cloraRepName,
    } = this.props;

    switch (action) {
      case 'Decline':
        return `Dear ${consultant},\n
Thank you for your interest in the <a href="${projectUrl}">${project} opportunity.</a>. We regret to inform you that the client has decided to move forward with a different candidate. \n
We appreciate the time and effort that you put into submitting your application, and encourage you to continue browsing and applying for roles through the <a href="${opportunitiesUrl}">Open Opportunities page</a>.\n
We will also make sure to reach out to you as new opportunities arise that match your skills!\n`;
      case 'Waitlist':
        return `Dear ${consultant},\n
Thank you for your interest in the <a href="${projectUrl}">${project} opportunity.</a>. We truly appreciate your interest and the time you took to share your background with us. \n
We’ve received a high volume of applications for this opportunity and are reviewing them in batches to ensure each one receives thoughtful consideration. Your application is still under active review, and we will be in touch as soon as we have an update regarding next steps. \n
If you have any questions in the meantime, feel free to reach out to us at support@clora.com.\n
Thank you again for your interest—we’ll follow up as soon as we have an update.\n
Warm regards,
${cloraRepName}\n`;
      case 'Accept':
        return `Dear ${consultant},\n
Thank you for submitting your interest for <a href="${projectUrl}">${project}</a>. We would like to schedule a 20-minute call with you so that I can share a few more details about the project and brief you on the next steps. Please feel free to select the best time directly in my calendar here - [clora rep to insert their meeting booking link]. \n
If you have any specific questions, please do not hesitate to reach out at support@clora.com. \n`;
      default:
        return null;
    }
  }

  render() {
    const {
      consultant, project, action, addlButtonStyle,
    } = this.props;
    const {
      message, internalFeedbacks, feedbackText, sendEmail,
    } = this.state;
    const placeholder = internalFeedbacks && internalFeedbacks.map((r) => r.label).includes('Other') ? 'Explanation (required)' : 'Explanation';
    let buttonStyles = '';
    switch (action) {
      case 'Accept':
        buttonStyles = 'primary btn btn-primary accept-resume-button';
        break;
      case 'Decline':
        buttonStyles = 'btn btn-danger decline-resume-button';
        break;
      case 'Waitlist':
        buttonStyles = 'secondary btn btn-secondary';
        break;
      default:
    }

    return (
      <ModalTrigger
        triggerText={action}
        buttonStyles={`${buttonStyles} ${addlButtonStyle}`}
        submitButtonText={action}
        handleSubmit={this.handleSubmit}
        triggerType="button"
        idKey={`${action.toLowerCase()}-resume`}
        submitDisabled={submitDisabled(action, internalFeedbacks, feedbackText, message)}
        closeAfterSubmit
      >
        <div className="style-guide">
          <div className="text-center mb-3">
            <h1>
              {`You're about to ${action.toLowerCase()} ${consultant} for the ${project}!`}
            </h1>
          </div>

          Indicate primary reason(s) you are
          {' '}
          {this.actionVerb()}
          {' '}
          this consultant&apos;s interest:
          <div className="mb-3">
            <AsyncSelect
              className="rejection-dropdown"
              styles={customStyles}
              classNamePrefix="clora-multiselect"
              loadOptions={internalFeedbackOptions}
              defaultOptions
              value={internalFeedbacks}
              maxMenuHeight={500}
              onChange={(val) => this.handleInputChange('internalFeedbacks', val)}
              closeOnSelect={false}
              closeMenuOnSelect={false}
              isMulti
              cacheOptions
              components={{ DropdownIndicator, IndicatorSeparator: () => null }}
            />
          </div>
          Internal notes about
          {' '}
          {action === 'Accept' ? 'accepted interest' : action.toLowerCase()}
          :
          <div className="action-explanation mb-25">
            <TextAreaInput
              textAreaPrompt=""
              placeholder={placeholder}
              value={feedbackText}
              onAnswerChange={(val) => this.handleInputChange('feedbackText', val)}
            />
          </div>
          <div className="option consultant-email-option-container d-flex gap-3 my-3">
            <span className="consultant-email-option-heading">Do you want to send an email?</span>
            <label className="custom-radio-container mr-15" htmlFor="email">
              <input
                type="radio"
                id="email"
                name="email"
                value
                checked={sendEmail}
                onChange={() => this.setState({ sendEmail: true })}
              />
              <span className="radio-btn radio-btn-red" />
              <span className="radio-text">Yes</span>
            </label>
            <label className="custom-radio-container" htmlFor="no-email">
              <input
                type="radio"
                id="no-email"
                name="no-email"
                value={false}
                checked={!sendEmail}
                onChange={() => this.setState({ sendEmail: false })}
              />
              <span className="radio-btn radio-btn-red" />
              <span className="radio-text">No</span>
            </label>
          </div>
          { sendEmail
            && (
            <div className="rejection-email-text">
              <TextAreaInput
                textAreaPrompt=""
                onAnswerChange={(val) => this.handleInputChange('message', val)}
                value={message}
              />
            </div>
            )}
        </div>
      </ModalTrigger>
    );
  }
}

ResumeActions.propTypes = {
  consultant: PropTypes.string.isRequired,
  project: PropTypes.string.isRequired,
  formUrl: PropTypes.string.isRequired,
  interestId: PropTypes.number.isRequired,
  csrfToken: PropTypes.string.isRequired,
  opportunitiesUrl: PropTypes.string.isRequired,
  projectUrl: PropTypes.string.isRequired,
  afterSubmit: PropTypes.func.isRequired,
  internalFeedbacks: PropTypes.arrayOf(PropTypes.string),
  feedbackText: PropTypes.string,
  action: PropTypes.oneOf(['Accept', 'Decline', 'Waitlist']).isRequired,
  addlButtonStyle: PropTypes.string,
};

ResumeActions.defaultProps = {
  internalFeedbacks: [],
  feedbackText: '',
  addlButtonStyle: '',
};

export default ResumeActions;
